import type { AppacGeometry, WingDefinition, WingStation } from "./api";

export type { WingDefinition, WingStation } from "./api";

export interface WingMetrics {
  semi_span: number;
  span: number;
  area: number;
  aspect_ratio: number;
}

export const QUICK_LIFTING_LINE_MAX_PANELS = 96;
/** @deprecated Use QUICK_LIFTING_LINE_MAX_PANELS for the relaxed free-wake solver. */
export const QUICK_LIFTING_LINE_MAX_HORSESHOES = QUICK_LIFTING_LINE_MAX_PANELS;
export const QUICK_LIFTING_LINE_MAX_STATIONS = QUICK_LIFTING_LINE_MAX_PANELS / 2 + 1;

interface NacaParameters {
  camber: number;
  camberPosition: number;
  thickness: number;
}

interface SampledStation extends Omit<WingStation, "airfoil"> {
  naca: NacaParameters;
}

/**
 * A modest, deliberately non-elliptic starting point for the wing editor.
 * Coordinates are metres in the APPAC convention: +x aft, +y span, +z up.
 */
export const DEFAULT_WING: WingDefinition = {
  schema_version: 1,
  name: "Cranked swept wing",
  symmetry: "half",
  chord_panels: 24,
  span_panels_per_segment: 8,
  stations: [
    {
      id: "root",
      y: 0,
      x_le: 0,
      z_le: 0,
      chord: 2.4,
      twist_deg: 2,
      airfoil: "NACA 2412",
    },
    {
      id: "kink",
      y: 3.2,
      x_le: 0.48,
      z_le: 0.16,
      chord: 1.62,
      twist_deg: 0,
      airfoil: "NACA 2412",
    },
    {
      id: "tip",
      y: 6,
      x_le: 1.56,
      z_le: 0.42,
      chord: 0.72,
      twist_deg: -2,
      airfoil: "NACA 0010",
    },
  ],
};

function nacaCode(airfoil: string): string | null {
  const code = airfoil.trim().replace(/^naca[\s_-]*/i, "");
  return /^\d{4}$/.test(code) ? code : null;
}

function parseNaca(airfoil: string): NacaParameters {
  const code = nacaCode(airfoil);
  if (!code) throw new Error(`Unsupported airfoil ${JSON.stringify(airfoil)}; expected NACA 4 digits`);
  return {
    camber: Number(code[0]) / 100,
    camberPosition: Number(code[1]) / 10,
    thickness: Number(code.slice(2)) / 100,
  };
}

export function validateWing(wing: WingDefinition): string[] {
  const errors: string[] = [];
  if (wing.schema_version !== 1) errors.push("schema_version must be 1");
  if (!wing.name.trim()) errors.push("name is required");
  if (wing.symmetry !== "half") errors.push("symmetry must be half");
  if (!Number.isInteger(wing.chord_panels) || wing.chord_panels < 4) {
    errors.push("chord_panels must be an integer of at least 4");
  }
  if (!Number.isInteger(wing.span_panels_per_segment) || wing.span_panels_per_segment < 1) {
    errors.push("span_panels_per_segment must be a positive integer");
  }
  if (!Array.isArray(wing.stations) || wing.stations.length < 2) {
    errors.push("at least two stations are required");
    return errors;
  }

  const ids = new Set<string>();
  wing.stations.forEach((station, index) => {
    const label = station.id.trim() || `#${index + 1}`;
    if (!station.id.trim()) errors.push(`station ${index + 1} needs an id`);
    if (ids.has(station.id)) errors.push(`station id ${JSON.stringify(station.id)} is duplicated`);
    ids.add(station.id);

    for (const field of ["y", "x_le", "z_le", "chord", "twist_deg"] as const) {
      if (!Number.isFinite(station[field])) errors.push(`station ${label} ${field} must be finite`);
    }
    if (Number.isFinite(station.y) && station.y < 0) errors.push(`station ${label} y must be non-negative`);
    if (Number.isFinite(station.chord) && station.chord <= 0) {
      errors.push(`station ${label} chord must be positive`);
    }
    if (!nacaCode(station.airfoil)) errors.push(`station ${label} airfoil must be a NACA 4-digit code`);

    const previous = wing.stations[index - 1];
    if (previous && Number.isFinite(previous.y) && Number.isFinite(station.y) && station.y <= previous.y) {
      errors.push(`station ${label} y must be greater than the preceding station y`);
    }
  });

  const root = wing.stations[0];
  if (root && Number.isFinite(root.y) && Math.abs(root.y) > 1e-9) {
    errors.push("the root station y must be 0 for half-wing symmetry");
  }
  return errors;
}

function assertValidWing(wing: WingDefinition): void {
  const errors = validateWing(wing);
  if (errors.length > 0) throw new Error(`Invalid wing: ${errors.join("; ")}`);
}

export function wingMetrics(wing: WingDefinition): WingMetrics {
  assertValidWing(wing);
  let halfArea = 0;
  for (let index = 0; index + 1 < wing.stations.length; index += 1) {
    const inner = wing.stations[index]!;
    const outer = wing.stations[index + 1]!;
    halfArea += (outer.y - inner.y) * (inner.chord + outer.chord) / 2;
  }
  const semi_span = wing.stations[wing.stations.length - 1]!.y;
  const span = semi_span * 2;
  const area = halfArea * 2;
  return {
    semi_span,
    span,
    area,
    aspect_ratio: span * span / area,
  };
}

function uniqueStationId(stations: WingStation[]): string {
  const ids = new Set(stations.map((station) => station.id));
  let suffix = 1;
  while (ids.has(`station-${suffix}`)) suffix += 1;
  return `station-${suffix}`;
}

function interpolateNumber(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function midpointStation(inner: WingStation, outer: WingStation, id: string): WingStation {
  return {
    id,
    y: interpolateNumber(inner.y, outer.y, 0.5),
    x_le: interpolateNumber(inner.x_le, outer.x_le, 0.5),
    z_le: interpolateNumber(inner.z_le, outer.z_le, 0.5),
    chord: interpolateNumber(inner.chord, outer.chord, 0.5),
    twist_deg: interpolateNumber(inner.twist_deg, outer.twist_deg, 0.5),
    airfoil: inner.airfoil,
  };
}

function extendedTipStation(previous: WingStation, tip: WingStation, id: string): WingStation {
  const extrapolatedChord = tip.chord + (tip.chord - previous.chord);
  return {
    id,
    y: tip.y + (tip.y - previous.y),
    x_le: tip.x_le + (tip.x_le - previous.x_le),
    z_le: tip.z_le + (tip.z_le - previous.z_le),
    chord: Math.max(tip.chord * 0.25, extrapolatedChord),
    twist_deg: tip.twist_deg + (tip.twist_deg - previous.twist_deg),
    airfoil: tip.airfoil,
  };
}

export function addWingStation(wing: WingDefinition, afterId: string): WingDefinition {
  const afterIndex = wing.stations.findIndex((station) => station.id === afterId);
  if (afterIndex < 0) return wing;
  const inner = wing.stations[afterIndex]!;
  const outer = wing.stations[afterIndex + 1];
  const id = uniqueStationId(wing.stations);
  const station = outer
    ? midpointStation(inner, outer, id)
    : extendedTipStation(wing.stations[afterIndex - 1]!, inner, id);
  const stations = wing.stations.map((item) => ({ ...item }));
  stations.splice(afterIndex + 1, 0, station);
  return { ...wing, stations };
}

/** Keep mirrored dense-kernel work bounded while preserving every design station. */
export function fitQuickLiftingLinePanelBudget(wing: WingDefinition): WingDefinition {
  const segmentCount = Math.max(1, wing.stations.length - 1);
  const maximumPanelsPerSegment = Math.max(
    1,
    Math.floor((QUICK_LIFTING_LINE_MAX_PANELS / 2) / segmentCount),
  );
  const spanPanelsPerSegment = Math.min(wing.span_panels_per_segment, maximumPanelsPerSegment);
  return spanPanelsPerSegment === wing.span_panels_per_segment
    ? wing
    : { ...wing, span_panels_per_segment: spanPanelsPerSegment };
}

export function removeWingStation(wing: WingDefinition, id: string): WingDefinition {
  if (wing.stations.length <= 2 || !wing.stations.some((station) => station.id === id)) return wing;
  return {
    ...wing,
    stations: wing.stations.filter((station) => station.id !== id).map((station) => ({ ...station })),
  };
}

export function updateWingStation(
  wing: WingDefinition,
  id: string,
  patch: Partial<Omit<WingStation, "id">>,
): WingDefinition {
  if (!wing.stations.some((station) => station.id === id)) return wing;
  const stations = wing.stations
    .map((station) => station.id === id ? { ...station, ...patch, id: station.id } : { ...station })
    .sort((a, b) => a.y - b.y);
  return { ...wing, stations };
}

function interpolateNaca(a: NacaParameters, b: NacaParameters, t: number): NacaParameters {
  return {
    camber: interpolateNumber(a.camber, b.camber, t),
    camberPosition: interpolateNumber(a.camberPosition, b.camberPosition, t),
    thickness: interpolateNumber(a.thickness, b.thickness, t),
  };
}

function interpolateStation(inner: WingStation, outer: WingStation, t: number): SampledStation {
  return {
    id: `${inner.id}:${outer.id}:${t}`,
    y: interpolateNumber(inner.y, outer.y, t),
    x_le: interpolateNumber(inner.x_le, outer.x_le, t),
    z_le: interpolateNumber(inner.z_le, outer.z_le, t),
    chord: interpolateNumber(inner.chord, outer.chord, t),
    twist_deg: interpolateNumber(inner.twist_deg, outer.twist_deg, t),
    naca: interpolateNaca(parseNaca(inner.airfoil), parseNaca(outer.airfoil), t),
  };
}

function spanRows(wing: WingDefinition): SampledStation[] {
  const rows: SampledStation[] = [];
  for (let segment = 0; segment + 1 < wing.stations.length; segment += 1) {
    const inner = wing.stations[segment]!;
    const outer = wing.stations[segment + 1]!;
    for (let panel = 0; panel < wing.span_panels_per_segment; panel += 1) {
      rows.push(interpolateStation(inner, outer, panel / wing.span_panels_per_segment));
    }
  }
  const tip = wing.stations[wing.stations.length - 1]!;
  rows.push({ ...tip, naca: parseNaca(tip.airfoil) });
  return rows;
}

function nacaSurfacePoint(parameters: NacaParameters, x: number, upper: boolean): [number, number] {
  const { camber: m, camberPosition: p, thickness: thicknessRatio } = parameters;
  let camber = 0;
  let camberSlope = 0;
  if (m > 0 && p > 0 && p < 1) {
    if (x < p) {
      camber = m / (p * p) * (2 * p * x - x * x);
      camberSlope = 2 * m / (p * p) * (p - x);
    } else {
      const oneMinusP = 1 - p;
      camber = m / (oneMinusP * oneMinusP) * ((1 - 2 * p) + 2 * p * x - x * x);
      camberSlope = 2 * m / (oneMinusP * oneMinusP) * (p - x);
    }
  }
  const thickness = x === 0 || x === 1
    ? 0
    : 5 * thicknessRatio * (
      0.2969 * Math.sqrt(x)
      - 0.1260 * x
      - 0.3516 * x * x
      + 0.2843 * x * x * x
      - 0.1036 * x * x * x * x
    );
  const theta = Math.atan(camberSlope);
  const sign = upper ? 1 : -1;
  return [
    x - sign * thickness * Math.sin(theta),
    camber + sign * thickness * Math.cos(theta),
  ];
}

function sectionRing(station: SampledStation, chordPanels: number): number[][] {
  const xs = Array.from(
    { length: chordPanels + 1 },
    (_, index) => 0.5 * (1 - Math.cos(Math.PI * index / chordPanels)),
  );
  const normalized: Array<[number, number]> = [];
  for (let index = chordPanels; index >= 0; index -= 1) {
    normalized.push(nacaSurfacePoint(station.naca, xs[index]!, true));
  }
  // LE and the single sharp trailing-edge point already belong to the upper path.
  for (let index = 1; index < chordPanels; index += 1) {
    normalized.push(nacaSurfacePoint(station.naca, xs[index]!, false));
  }

  const twist = station.twist_deg * Math.PI / 180;
  const cosine = Math.cos(twist);
  const sine = Math.sin(twist);
  return normalized.map(([x, z]) => {
    // x_le/z_le are the physical leading edge. Rotate the normalized section
    // from that point; the backend derives its quarter chord from the same
    // convention, so twist cannot silently move the declared LE.
    const dx = x * station.chord;
    const dz = z * station.chord;
    return [
      station.x_le + cosine * dx + sine * dz,
      station.y,
      station.z_le - sine * dx + cosine * dz,
    ];
  });
}

/** Build a closed, mirrored preview mesh from the half-wing station definition. */
export function buildWingPreview(wing: WingDefinition): AppacGeometry {
  assertValidWing(wing);
  const positiveRows = spanRows(wing);
  const rows = [
    ...positiveRows.slice(1).reverse().map((station) => ({ ...station, y: -station.y })),
    ...positiveRows,
  ];
  const points = rows.flatMap((station) => sectionRing(station, wing.chord_panels));
  const ringSize = wing.chord_panels * 2;
  const cells: number[][] = [];
  for (let row = 0; row + 1 < rows.length; row += 1) {
    const innerOffset = row * ringSize;
    const outerOffset = (row + 1) * ringSize;
    for (let point = 0; point < ringSize; point += 1) {
      const next = (point + 1) % ringSize;
      cells.push([
        innerOffset + point,
        innerOffset + next,
        outerOffset + next,
        outerOffset + point,
      ]);
    }
  }
  cells.push(Array.from({ length: ringSize }, (_, index) => ringSize - 1 - index));
  const tipOffset = (rows.length - 1) * ringSize;
  cells.push(Array.from({ length: ringSize }, (_, index) => tipOffset + index));
  return { points, cells };
}
